/**
 * Parse a YouTube/Vimeo URL for privacy-friendly embedding (Phase 3d).
 * - YouTube → youtube-nocookie.com (no tracking cookies until playback)
 * - Vimeo → player with dnt=1 (do not track)
 * Returns null if the URL isn't a recognized YouTube/Vimeo video (the backend
 * already allowlists hosts, but we parse defensively here too).
 */

interface ParsedVideo {
  provider: 'youtube' | 'vimeo';
  id: string;
}

function parse(raw?: string | null): ParsedVideo | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase().replace(/^www\./, '');

  if (host === 'youtu.be') {
    const id = url.pathname.slice(1);
    return id ? { provider: 'youtube', id } : null;
  }
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    const id = url.searchParams.get('v') || (url.pathname.startsWith('/embed/') ? url.pathname.split('/')[2] : '');
    return id ? { provider: 'youtube', id } : null;
  }
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean).pop();
    return id && /^\d+$/.test(id) ? { provider: 'vimeo', id } : null;
  }
  return null;
}

export function toEmbedUrl(raw?: string | null): string | null {
  const v = parse(raw);
  if (!v) return null;
  return v.provider === 'youtube'
    ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(v.id)}`
    : `https://player.vimeo.com/video/${v.id}?dnt=1`;
}

/**
 * Poster thumbnail URL for the click-to-load facade.
 * - YouTube: served from img.youtube.com (a third-party IMAGE request on load —
 *   leaks IP/referrer but no tracking JS/cookies, unlike the full player).
 *   maxresdefault isn't guaranteed to exist; the component falls back to
 *   hqdefault (see `youTubeThumbnailFallback`) on error.
 * - Vimeo: no static id-based thumbnail (needs an API call) → null, so the
 *   component keeps the branded gradient facade.
 */
export function getVideoThumbnail(raw?: string | null): string | null {
  const v = parse(raw);
  if (!v || v.provider !== 'youtube') return null;
  return `https://img.youtube.com/vi/${encodeURIComponent(v.id)}/maxresdefault.jpg`;
}

export function youTubeThumbnailFallback(raw?: string | null): string | null {
  const v = parse(raw);
  if (!v || v.provider !== 'youtube') return null;
  return `https://img.youtube.com/vi/${encodeURIComponent(v.id)}/hqdefault.jpg`;
}
